import styled, { css } from 'styled-components';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Chip from '@mui/material/Chip';

import colors from '../../../../../styles/colors';
import {
  StyledInput,
  StyledPriceSelector,
  UpsellContainer,
} from '../../../../components/ContractModal/ContractConfigurator/styles';

const ContentWrapper = styled.div`
  padding: 16px;
  height: 100%;
  width: 75%;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  background-color: ${colors.base.gamma};
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.shades.gamma5};
  border-radius: 8px;
  padding: 16px;
  margin: 12px 0;
  background-color: ${colors.base.alpha};
`;

const SectionTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
`;

const SectionDescription = styled.div`
  font-size: 13px;
  color: ${colors.shades.gamma7};
  margin-top: 2px;
`;

const TouchpointContainer = styled.div`
  border: 1px solid ${colors.shades.gamma5};
  border-radius: 8px;
  padding: 14px;
  margin-top: 12px;
`;

const ExperimentContainer = styled(TouchpointContainer)`
  border-style: dashed;
  border-color: ${colors.shades.beta2};
  background-color: ${colors.base.gamma};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const OfferRow = styled(UpsellContainer)`
  margin-top: 8px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

/*
  Challenge offers carry five fields, which crowd on a 16" screen — the program
  and meal plan selectors get their own row below the product/price/duration one.
*/
const OfferSecondaryRow = styled(OfferRow)`
  margin-top: 16px;
`;

/*
  The height the product selector comes out at: its 14px line-height plus 15px
  of padding and a 1px border, top and bottom (components/Select/styles.js).
  The price and duration fields sit in a row beside it and have to match.
*/
const OFFER_FIELD_HEIGHT_PX = 46;

/*
  The generator shrinks its price and term inputs to a 29px inner height for its
  dense contract rows, so here they have to be resized to the selector's height.

  Pin the outlined-input *root*, not the inner input. MUI adds `8.5px 14px` of
  padding to a small input on top of whatever height it is given, so asking for
  46px inside produced a 63px control — and a different height again on the
  duration field, whose unit-picker adornment carries its own padding. Sizing
  the root instead and letting the input fill it makes both fields exactly
  OFFER_FIELD_HEIGHT_PX whatever they contain. The outline is a fieldset drawn
  over the control rather than a border in flow, so it costs no height either way.

  Scoped to these two fields: the product selector already has this height by
  its own rules, and a rule on the shared input root would stretch it.
*/
const offerFieldHeight = css`
  .MuiOutlinedInput-root {
    height: ${OFFER_FIELD_HEIGHT_PX}px;
  }

  .MuiOutlinedInput-input {
    /* MUI sets the input content-box, so without this the padding would be
       added to the full height again and overflow the root. */
    box-sizing: border-box;
    height: 100%;
    /* The price field pins a 29px line-height for the dense row; at this height
       that sits the number high, and the browser centres it without one. */
    line-height: normal;
  }
`;

const OfferPriceSelector = styled(StyledPriceSelector)`
  ${offerFieldHeight}
`;

const OfferInput = styled(StyledInput)`
  ${offerFieldHeight}
`;

const OfferTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.shades.gamma7};
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid ${colors.shades.gamma5};
`;

const SearchField = styled(TextField)`
  width: 320px;
  margin-top: 12px;
`;

const StyledTextField = styled(TextField)`
  width: 100%;
  margin-top: 12px;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  margin-left: 0;
`;

const StyledChip = styled(Chip)`
  &.custom {
    background-color: ${colors.shades.beta2};
    color: ${colors.base.alpha};
  }
  &.experiment {
    margin-left: 6px;
  }
`;

const ChallengeRow = styled.div`
  border: 1px solid ${colors.shades.gamma5};
  border-radius: 8px;
  padding: 14px;
  margin-top: 10px;
  background-color: ${({ $isExpanded }) => ($isExpanded ? colors.base.gamma : colors.base.alpha)};
  transition: background-color 250ms ease;
`;

const CollapseHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
`;

const Chevron = styled.div`
  display: flex;
  align-items: center;
  color: ${colors.shades.gamma7};
  transform: rotate(${({ $isExpanded }) => ($isExpanded ? '180deg' : '0deg')});
  transition: transform 150ms ease;
`;

const SectionActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const MediaHint = styled.div`
  font-size: 12px;
  color: ${colors.shades.gamma7};
  margin-top: 4px;
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin: 16px 0 32px;
`;

export {
  ContentWrapper,
  SectionContainer,
  SectionTitle,
  SectionDescription,
  OfferRow,
  OfferSecondaryRow,
  OfferPriceSelector,
  OfferInput,
  OfferTitle,
  TouchpointContainer,
  ExperimentContainer,
  Row,
  StyledTextField,
  StyledFormControlLabel,
  SearchField,
  StyledChip,
  ChallengeRow,
  CollapseHeader,
  Chevron,
  SectionActions,
  MediaHint,
  ActionsRow,
};
